import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';

import { CommonSettingsDataService } from '../infrastructure/common-settings-data.service';
import { PaymentMethodSettingsDataService } from '../infrastructure/payment-method-settings-data.service';
import { getPaymentMethodSettingsTransformedData } from '../utils';
import { getCommonSettingsCollectedData, getCommonSettingsTransformedData } from '../utils/common-settings-data';

import * as PlatformSettingsActions from './platform-settings.actions';

@Injectable()
export class PlatformSettingsEffects {
    private readonly actions$ = inject(Actions);
    private readonly paymentMethodSettingsDataService = inject(PaymentMethodSettingsDataService);
    private readonly commonSettingsDataService = inject(CommonSettingsDataService);

    loadPaymentMethodSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlatformSettingsActions.loadPaymentMethods),
            switchMap(() =>
                this.paymentMethodSettingsDataService.getPaymentMethodSettings().pipe(
                    map((response) => {
                        const paymentMethods = getPaymentMethodSettingsTransformedData(response.paymentMethodSettings);

                        return PlatformSettingsActions.loadPaymentMethodsSuccess({ paymentMethods });
                    }),
                    catchError((error) => of(PlatformSettingsActions.loadPaymentMethodsFailure({ error })))
                )
            )
        )
    );

    loadCommonSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlatformSettingsActions.loadCommon),
            switchMap(() =>
                this.commonSettingsDataService.getCommonSettings().pipe(
                    map((response) => {
                        const common = getCommonSettingsTransformedData(response);

                        return PlatformSettingsActions.loadCommonSuccess({ common });
                    }),
                    catchError((error) => of(PlatformSettingsActions.loadCommonFailure({ error })))
                )
            )
        )
    );

    updatePaymentMethodSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlatformSettingsActions.updatePaymentMethods),
            switchMap(({ paymentMethodSettings }) =>
                this.paymentMethodSettingsDataService.updatePaymentMethodSettings(paymentMethodSettings).pipe(
                    map(() =>
                        PlatformSettingsActions.updatePaymentMethodsSuccess({ paymentMethods: paymentMethodSettings })
                    ),
                    catchError((error) => of(PlatformSettingsActions.updatePaymentMethodsFailure({ error })))
                )
            )
        )
    );

    updateCommonSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlatformSettingsActions.updateCommon),
            switchMap(({ commonSettings }) =>
                this.commonSettingsDataService.updateCommonSettings(commonSettings).pipe(
                    map(() =>
                        PlatformSettingsActions.updateCommonSuccess({
                            commonSettings: getCommonSettingsCollectedData(commonSettings)
                        })
                    ),
                    catchError((error) => of(PlatformSettingsActions.updateCommonFailure({ error })))
                )
            )
        )
    );
}
